import { useState, useCallback } from 'react';

/**
 * Hook per validazione form client-side
 * 
 * @example
 * const { errors, validate, validateField, clearErrors } = useFormValidation({
 *   email: { required: true, email: true },
 *   password: { required: true, minLength: 6 },
 *   amount: { required: true, number: true, min: 0 }
 * });
 */
export function useFormValidation(rules = {}) {
  const [errors, setErrors] = useState({});

  const validators = {
    required: (value, message) => {
      if (value === undefined || value === null || value === '' || 
          (Array.isArray(value) && value.length === 0)) {
        return message || 'Campo obbligatorio';
      }
      return null;
    },
    
    email: (value, message) => {
      if (!value) return null;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return message || 'Email non valida';
      }
      return null;
    },
    
    minLength: (value, min, message) => {
      if (!value) return null;
      if (String(value).length < min) {
        return message || `Minimo ${min} caratteri`;
      }
      return null;
    },
    
    maxLength: (value, max, message) => {
      if (!value) return null;
      if (String(value).length > max) {
        return message || `Massimo ${max} caratteri`;
      }
      return null;
    },
    
    number: (value, message) => {
      if (!value && value !== 0) return null;
      if (isNaN(Number(value))) {
        return message || 'Deve essere un numero';
      }
      return null;
    },
    
    min: (value, min, message) => {
      if (!value && value !== 0) return null;
      if (Number(value) < min) {
        return message || `Valore minimo: ${min}`;
      }
      return null;
    },
    
    max: (value, max, message) => {
      if (!value && value !== 0) return null;
      if (Number(value) > max) {
        return message || `Valore massimo: ${max}`;
      }
      return null;
    },
    
    pattern: (value, regex, message) => {
      if (!value) return null;
      if (!regex.test(value)) {
        return message || 'Formato non valido';
      }
      return null;
    },
    
    date: (value, message) => {
      if (!value) return null;
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return message || 'Data non valida';
      }
      return null;
    },
    
    dateAfter: (value, afterDate, message) => {
      if (!value) return null;
      const date = new Date(value);
      const after = new Date(afterDate);
      if (date <= after) {
        return message || `La data deve essere successiva a ${afterDate}`;
      }
      return null;
    },
    
    iban: (value, message) => {
      if (!value) return null;
      // Basic IBAN validation (Italian format)
      const ibanRegex = /^IT\d{2}[A-Z]\d{10}[A-Z0-9]{12}$/i;
      const cleanValue = value.replace(/\s/g, '');
      if (!ibanRegex.test(cleanValue)) {
        return message || 'IBAN non valido';
      }
      return null;
    },
    
    codiceFiscale: (value, message) => {
      if (!value) return null;
      const cfRegex = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i;
      if (!cfRegex.test(value)) {
        return message || 'Codice Fiscale non valido';
      }
      return null;
    },
    
    partitaIva: (value, message) => {
      if (!value) return null;
      const pivaRegex = /^\d{11}$/;
      if (!pivaRegex.test(value)) {
        return message || 'Partita IVA non valida';
      }
      return null;
    }
  };

  const validateField = useCallback((fieldName, value) => {
    const fieldRules = rules[fieldName];
    if (!fieldRules) return null;

    for (const [ruleName, ruleConfig] of Object.entries(fieldRules)) {
      const validator = validators[ruleName];
      if (!validator) continue;

      let error = null;
      
      if (typeof ruleConfig === 'boolean' && ruleConfig) {
        error = validator(value);
      } else if (typeof ruleConfig === 'object') {
        error = validator(value, ruleConfig.value, ruleConfig.message);
      } else if (typeof ruleConfig === 'number' || typeof ruleConfig === 'string') {
        error = validator(value, ruleConfig);
      }

      if (error) {
        setErrors(prev => ({ ...prev, [fieldName]: error }));
        return error;
      }
    }

    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
    return null;
  }, [rules]);

  const validate = useCallback((data) => {
    const newErrors = {};
    let isValid = true;

    for (const [fieldName, fieldRules] of Object.entries(rules)) {
      const value = data[fieldName];
      
      for (const [ruleName, ruleConfig] of Object.entries(fieldRules)) {
        const validator = validators[ruleName];
        if (!validator) continue;

        let error = null;
        
        if (typeof ruleConfig === 'boolean' && ruleConfig) {
          error = validator(value);
        } else if (typeof ruleConfig === 'object') {
          error = validator(value, ruleConfig.value, ruleConfig.message);
        } else if (typeof ruleConfig === 'number' || typeof ruleConfig === 'string') {
          error = validator(value, ruleConfig);
        }

        if (error) {
          newErrors[fieldName] = error;
          isValid = false;
          break;
        }
      }
    }

    setErrors(newErrors);
    return isValid;
  }, [rules]);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const clearFieldError = useCallback((fieldName) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  return {
    errors,
    validate,
    validateField,
    clearErrors,
    clearFieldError,
    hasErrors: Object.keys(errors).length > 0
  };
}

export default useFormValidation;
